import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  fetchLists,
  addListOption,
  extractReceipt,
  createReceipt,
  updateReceipt,
  getReceipt,
  pdfUrl,
} from "@/lib/api";
import { imgSrc } from "@/lib/format";
import { HTTP_CONFLICT, HTTP_UNSUPPORTED_MEDIA } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  EMPTY_FORM,
  UNSUPPORTED_IMG_MSG,
  readFileAsDataUrl,
  to2,
} from "@/components/receipt/receiptUtils";
import { UploadStep } from "@/components/receipt/UploadStep";
import { ReceiptImagePanel } from "@/components/receipt/ReceiptImagePanel";
import { ExtractedDataFields } from "@/components/receipt/ExtractedDataFields";
import { EnrichmentFields } from "@/components/receipt/EnrichmentFields";

export default function ReceiptEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // step: 'upload' | 'extracting' | 'enrich'
  const [step, setStep] = useState(isEdit ? "enrich" : "upload");
  const [imageBase64, setImageBase64] = useState(""); // raw, no prefix
  const [imageMime, setImageMime] = useState("image/jpeg");
  const [previewSrc, setPreviewSrc] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const [lists, setLists] = useState({ purpose: [], person: [], payment_method: [] });
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedId, setSavedId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(isEdit);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Editable VAT breakdown: keep the single-field VAT total in sync with the
  // sum of the per-rate lines (mixed rates -> vat_rate 0).
  const setVatBreakdown = (rows) =>
    setForm((f) => {
      const vatSum = rows.reduce((s, l) => s + (Number(l.vat) || 0), 0);
      return {
        ...f,
        vat_breakdown: rows,
        ...(rows.length >= 2 ? { vat_amount: to2(vatSum), vat_rate: "0" } : {}),
      };
    });

  const loadLists = useCallback(async () => {
    try {
      const data = await fetchLists();
      setLists(data);
    } catch {
      toast.error("Stammdaten konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await getReceipt(id);
        setImageBase64(r.image_base64);
        setImageMime(r.image_mime || "image/jpeg");
        setPreviewSrc(imgSrc(r.image_base64, r.image_mime));
        setForm({
          supplier: r.supplier || "",
          invoice_number: r.invoice_number || "",
          date: r.date || "",
          price: to2(r.price ?? ""),
          vat_amount: to2(r.vat_amount ?? ""),
          vat_rate: r.vat_rate ?? "",
          vat_breakdown: r.vat_breakdown || [],
          currency: r.currency || "EUR",
          tip: to2(r.tip ?? ""),
          purpose: r.purpose || "",
          purpose_other: r.purpose_other || "",
          performed_by: r.performed_by || "",
          performed_by_other: r.performed_by_other || "",
          paid_by: r.paid_by || "",
          paid_by_other: r.paid_by_other || "",
          paid_with: r.paid_with || "",
          paid_with_other: r.paid_with_other || "",
          note: r.note || "",
        });
        setSavedId(r.id);
      } catch {
        toast.error("Beleg konnte nicht geladen werden.");
        navigate("/");
      } finally {
        setLoadingReceipt(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const runExtraction = async (dataUrl, mime) => {
    setPreviewSrc(dataUrl);
    setStep("extracting");
    try {
      const res = await extractReceipt(dataUrl, mime);
      const ex = res.extracted || {};
      setImageBase64(res.image_base64);
      setImageMime(res.image_mime || "image/jpeg");
      setPreviewSrc(imgSrc(res.image_base64, res.image_mime));
      setForm((f) => ({
        ...f,
        supplier: ex.supplier || "",
        invoice_number: ex.invoice_number || "",
        date: ex.date || "",
        price: to2(ex.price ?? ""),
        vat_amount: to2(ex.vat_amount ?? ""),
        vat_rate: ex.vat_rate ?? "",
        vat_breakdown: ex.vat_breakdown || [],
        currency: ex.currency || "EUR",
        tip: to2(ex.tip ?? ""),
      }));
      toast.success("Beleg ausgelesen. Bitte Daten prüfen und anreichern.");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === HTTP_UNSUPPORTED_MEDIA) {
        toast.error(detail || UNSUPPORTED_IMG_MSG);
      } else {
        toast.error(
          `${detail || "Automatisches Auslesen fehlgeschlagen."} Bitte Felder manuell ausfüllen.`
        );
      }
      // keep uploaded image so user can fill manually
      const raw = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      setImageBase64(raw);
      setImageMime(mime);
    } finally {
      setStep("enrich");
    }
  };

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    // No strict client-side MIME guard: HEIC files are often reported with an
    // empty type by the browser. The backend validates the format and returns a
    // clear message for unsupported files (and the user can still continue).
    const dataUrl = await readFileAsDataUrl(file);
    await runExtraction(dataUrl, file.type || "image/jpeg");
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await handleFiles(e.dataTransfer.files);
  };

  const buildPayload = () => ({
    image_base64: imageBase64,
    image_mime: imageMime,
    supplier: form.supplier,
    invoice_number: form.invoice_number,
    date: form.date,
    price: Number(form.price) || 0,
    vat_amount: Number(form.vat_amount) || 0,
    vat_rate: Number(form.vat_rate) || 0,
    vat_breakdown:
      (form.vat_breakdown || []).length >= 2
        ? form.vat_breakdown.map((l) => ({
            rate: Number(l.rate) || 0,
            net: Number(l.net) || 0,
            vat: Number(l.vat) || 0,
          }))
        : [],
    currency: form.currency || "EUR",
    tip: Number(form.tip) || 0,
    purpose: form.purpose,
    purpose_other: form.purpose === "Sonstiges" ? form.purpose_other : "",
    performed_by: form.performed_by,
    performed_by_other: form.performed_by === "Sonstiges" ? form.performed_by_other : "",
    paid_by: form.paid_by,
    paid_by_other: form.paid_by === "Sonstiges" ? form.paid_by_other : "",
    paid_with: form.paid_with,
    paid_with_other: form.paid_with === "Sonstiges" ? form.paid_with_other : "",
    note: form.note,
  });

  const handleSave = async (goBack = true) => {
    if (!imageBase64) {
      toast.error("Kein Belegbild vorhanden.");
      return null;
    }
    setSaving(true);
    try {
      let result;
      if (savedId) {
        result = await updateReceipt(savedId, buildPayload());
      } else {
        result = await createReceipt(buildPayload());
        setSavedId(result.id);
      }
      toast.success("Beleg gespeichert.");
      if (goBack) navigate(isEdit ? `/beleg/${id}` : "/");
      return result;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Speichern fehlgeschlagen.";
      toast.error(detail);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNew = async () => {
    const result = await handleSave(false);
    if (!result) return;
    if (isEdit) {
      navigate("/neu");
      return;
    }
    // Reset to a fresh capture in-place (stay on the editor for the next receipt).
    setForm(EMPTY_FORM);
    setImageBase64("");
    setImageMime("image/jpeg");
    setPreviewSrc("");
    setSavedId(null);
    setStep("upload");
  };

  const handleExportPdf = async () => {
    let targetId = savedId;
    if (!targetId) {
      const result = await handleSave(false);
      targetId = result?.id;
    } else {
      // persist latest edits before exporting
      await handleSave(false);
    }
    if (!targetId) return;
    const a = document.createElement("a");
    a.href = pdfUrl(targetId);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const addOption = async (type, value) => {
    // Optimistically insert the option locally so the matching SelectItem
    // exists immediately (avoids the dropdown losing the just-added value).
    setLists((prev) => {
      const exists = (prev[type] || []).some((o) => o.value === value);
      if (exists) return prev;
      return { ...prev, [type]: [...(prev[type] || []), { id: `tmp-${value}`, value }] };
    });
    try {
      await addListOption(type, value);
      await loadLists();
      toast.success(`„${value}" hinzugefügt.`);
    } catch (e) {
      if (e?.response?.status === HTTP_CONFLICT) {
        toast.message("Eintrag existiert bereits.");
      } else {
        toast.error("Hinzufügen fehlgeschlagen.");
        await loadLists();
      }
    }
  };

  // ---------------- LOADING (edit mode) ----------------
  if (isEdit && loadingReceipt) {
    return (
      <div data-testid="editor-loading">
        <div className="h-5 w-40 bg-muted animate-pulse rounded-sm mb-6" />
        <div className="h-10 w-72 bg-muted animate-pulse rounded-sm mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 h-96 bg-muted animate-pulse rounded-sm" />
          <div className="md:col-span-7 h-96 bg-muted animate-pulse rounded-sm" />
        </div>
      </div>
    );
  }

  // ---------------- BLOCKED (wallet empty, new receipt) ----------------
  if (!isEdit && user?.topup_blocked) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="editor-blocked">
        <button
          onClick={() => navigate("/")}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
        </button>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
          <h1 className="font-heading text-2xl font-medium text-destructive">Erfassung gesperrt</h1>
          <p className="text-muted-foreground mt-2">
            Das Guthaben ist aufgebraucht. Neue Belege können erst nach einer Aufladung des
            Guthabens erfasst werden. Vorhandene Belege bleiben einseh- und exportierbar.
          </p>
          {user?.requires_topup && (
            <Button className="mt-5" onClick={() => navigate("/abo")} data-testid="editor-blocked-topup">
              Zum Guthaben
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ---------------- UPLOAD STEP ----------------
  if (step === "upload") {
    return (
      <UploadStep
        onBack={() => navigate("/")}
        dragOver={dragOver}
        setDragOver={setDragOver}
        onDrop={onDrop}
        fileRef={fileRef}
        cameraRef={cameraRef}
        handleFiles={handleFiles}
      />
    );
  }

  // ---------------- SPLIT VIEW (extracting + enrich) ----------------
  return (
    <div data-testid="enrichment-screen">
      <button
        onClick={() => navigate("/")}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-6 transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Übersicht
      </button>

      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">
            {isEdit ? "Beleg bearbeiten" : "Beleg anreichern"}
          </h1>
          <p className="text-muted-foreground mt-1">
            Prüfen Sie die ausgelesenen Daten und ergänzen Sie die Angaben.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        <ReceiptImagePanel
          previewSrc={previewSrc}
          step={step}
          isEdit={isEdit}
          onChangeImage={() => {
            setStep("upload");
            setPreviewSrc("");
            setImageBase64("");
            setForm(EMPTY_FORM);
          }}
        />

        {/* RIGHT: form */}
        <div className="md:col-span-7 space-y-6">
          <form
            data-testid="enrichment-form"
            onKeyDown={(e) => {
              // Return darf den Beleg nicht absenden; in Textareas (Notiz) bleibt Enter erlaubt.
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault();
              }
            }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(true);
            }}
            className="space-y-6"
          >
            <ExtractedDataFields
              form={form}
              setField={setField}
              step={step}
              setVatBreakdown={setVatBreakdown}
            />

            <EnrichmentFields form={form} setField={setField} lists={lists} addOption={addOption} />

            <div className="flex flex-wrap gap-3 sticky bottom-0 bg-background/95 backdrop-blur border-t border-border py-3 -mx-1 px-1">
              <Button
                type="submit"
                disabled={saving || step === "extracting"}
                className="gap-2"
                data-testid="save-receipt-button"
              >
                <Save className="h-4 w-4" />
                {saving ? "Speichern…" : "Speichern & Schließen"}
              </Button>
              <Button
                type="button"
                disabled={saving || step === "extracting"}
                onClick={handleSaveAndNew}
                className="gap-2"
                data-testid="save-and-new-button"
              >
                <Plus className="h-4 w-4" /> Speichern &amp; Neu
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={saving || step === "extracting"}
                onClick={handleExportPdf}
                className="gap-2 border border-border"
                data-testid="export-pdf-button"
              >
                <FileDown className="h-4 w-4" /> Als PDF exportieren
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate("/")}
                className="border border-border"
                data-testid="cancel-button"
              >
                Abbrechen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
